import { FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ClassNames from "classnames";
import Loader from "../../components/loader";
import { Modal } from "../../components";
import { issues } from "../../constants";
import { Issue, Platform } from "../../types/constants";
import { UserStory, UserstoryTasks } from "../../types/epic";
import {
  useCreateTask,
  useDeleteTask,
  useEditTask,
  useGetEpic,
  useGetEpicUserstories,
  useGetMovie,
  useQCDone,
  useUpdateEpicSubtitle,
} from "../../queries/epic";
import { MdModeEditOutline } from "react-icons/md";
import endpoints from "../../core/endpoints";
import convertSeconds from "../../utils/convertSecond";
import captureVideo from "../../utils/captureVideo";
import MovieInfo from "../../components/MovieInfo";
import "./styles.scss";

const { REACT_APP_BASE_URL: baseUrl, REACT_APP_IMG_URL: imgUrl } = process.env;

export default function EpicPage() {
  const navigate = useNavigate();
  // const platform: Platform = JSON.parse(Cookies.get("platform") || "{}");
  const platform: Platform = JSON.parse(localStorage.getItem('platform') || "{}")
  const param = useParams();
  const textareaRef = useRef<any>(null);
  const [userstories, setUserstories] = useState<UserStory[]>();
  const titleInputRef = useRef<any>(null);
  const subtitleInputRef = useRef<any>(null);
  const selectedTask = useRef<UserstoryTasks>();
  const playerTimeout = useRef<any>(null);
  const { mutate: getEpicData, data: epicData } = useGetEpic();
  const { mutate: createTaskRequest, data: createTaskData } = useCreateTask();
  const { mutate: deleteTaskRequest, data: deleteTaskData } = useDeleteTask();
  const { mutate: editTaskRequest, data: editTaskData } = useEditTask();
  const { mutate: getMovieRequest, data: getMovieData } = useGetMovie();
  const { mutate: updateEpic, data: updateData } = useUpdateEpicSubtitle();
  const {
    mutate: getUserstories,
    data: userstoriesData,
    isLoading,
  } = useGetEpicUserstories();
  const {
    mutate: qcDoneRequest,
    data: qcDoneData,
    error: qcDoneError,
  } = useQCDone();

  const checkRomeoExist = () => {
    if (!window.romeo) {
      playerTimeout.current = setTimeout(checkRomeoExist, 1000);
    }
    clearTimeout(playerTimeout.current);
    const playerOptions: any = {
      tracks: [],
      romeoStatXhrDisable: true,
      showSpeed: true,
      resumeUID: epicData?.factory_movie_id,
      lang: "fa",
      multiSRC: [
        [
          {
            type: "application/vnd.apple.mpegurl",
            src: getMovieData,
          },
        ],
      ],
    };
    if (epicData?.factory_subtitle_id) {
      playerOptions["tracks"].push({
        default: true,
        kind: "captions",
        src: endpoints.epic.getSubtitle(epicData.factory_subtitle_id),
        srclang: "fa",
        label: "فارسی",
      });
    }
    window.romeoPlayer = window.romeo("player", playerOptions);
  };

  const loadSubtitle = () => {
    const video = document.querySelector("video");
    let subText = "";
    if (!video) return;

    if (
      video.textTracks &&
      video.textTracks[0] &&
      video.textTracks[0].activeCues &&
      video.textTracks[0].activeCues[0]
    ) {
      const activeSub = video.textTracks[0].activeCues[0] as any;
      subText = activeSub.text;
    }

    textareaRef.current.value = subText;
  };

  const handleCreateTask = async (issue: Issue) => {
    if (issue.slug === "loadSubtitle") {
      loadSubtitle();
      return;
    }
    if (!textareaRef.current.value) {
      toast("شرح مشکل نمی‌تواند خالی باش");
      return;
    }
    const file = await captureVideo(window.player);
    const form = new FormData();
    const playerCurrentTime = document.querySelector("video")?.currentTime;
    if (!playerCurrentTime) return;

    const time = convertSeconds(playerCurrentTime);

    form.append("subject", textareaRef.current.value);
    form.append("image", file);
    form.append("project", platform.id.toString());
    form.append("time", time);
    form.append("category", issue.title);
    form.append("epic", param.id!);

    createTaskRequest({
      formData: form,
    });
  };

  const handleDeleteTask = (taskId: number) => {
    deleteTaskRequest({ id: taskId });
  };

  const handleQCDone = () => {
    if (!epicData?.factory_movie_id) return;
    qcDoneRequest({ movieId: epicData?.factory_movie_id, epicId: epicData.id });
  };

  const handleSubmitTitle = (e: FormEvent) => {
    e.preventDefault();
    if (!titleInputRef.current.value || !selectedTask.current) return;
    editTaskRequest({
      id: selectedTask.current.id,
      title: titleInputRef.current.value,
      version: selectedTask.current.version,
    });
  };

  const handleSubmitSubtitle = (e: FormEvent) => {
    e.preventDefault();
    if (!epicData) return;
    if (!subtitleInputRef.current?.value) {
      toast("لطفا ID را به درستی وارد کنید");
      return;
    }

    updateEpic({
      id: epicData.id,
      version: epicData.version,
      subId: subtitleInputRef.current?.value,
    });
  };

  const toggleTitleModal = (title: string) => {
    titleInputRef.current.value = title;
    const modalHandler = document?.querySelector("#editModal") as HTMLElement;
    modalHandler.click();
    return;
  };

  const toggleQcDoneModal = () => {
    const qcDoneModal = document.querySelector("#qcDoneModal") as HTMLElement;
    qcDoneModal.click();
    return;
  };

  const handleKeyup = (e: KeyboardEvent) => {
    if (e.key.toLowerCase() === "escape") {
      const editSubtitleModal = document.querySelector(
        "#editSubtitleModal"
      ) as HTMLInputElement;
      editSubtitleModal.checked = false;

      const modalHandler = document?.querySelector(
        "#editModal"
      ) as HTMLInputElement;
      modalHandler.checked = false;
    }
  };

  // Initial UseEffect
  useEffect(() => {
    if (!param.id) return;
    getEpicData({ epicId: Number(param.id) });
  }, [getEpicData, param.id]);

  useEffect(() => {
    if (epicData && epicData?.factory_movie_id) {
      getMovieRequest({ movieId: epicData?.factory_movie_id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [epicData, getMovieRequest]);

  useEffect(() => {
    getUserstories({ id: Number(param.id!) });
  }, [getUserstories, param.id]);

  useEffect(() => {
    if (userstoriesData) {
      setUserstories(userstoriesData);
    }
  }, [userstoriesData]);

  useEffect(() => {
    if (!getMovieData) return;
    checkRomeoExist();

    return () => {
      clearTimeout(playerTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMovieData]);

  useEffect(() => {
    if (createTaskData) {
      toast("تسک با موفقیت اضافه شد");
      textareaRef.current.value = "";
      getUserstories({ id: Number(param.id) });
    }
  }, [createTaskData, getUserstories, param.id]);


  useEffect(() => {
    if (deleteTaskData?.status === 204) {
      toast("تسک با موفقیت حذف شد");
      getUserstories({ id: Number(param.id!) });
    }
  }, [deleteTaskData, getUserstories, param.id]);

  useEffect(() => {
    if (qcDoneData?.status === 200) {
      toggleQcDoneModal();
      navigate("/epics");
    }
  }, [navigate, qcDoneData]);

  useEffect(() => { }, [qcDoneError]);

  useEffect(() => {
    if (updateData?.status === 200) {
      getEpicData({ epicId: Number(param.id) });
      const modalHandler = document?.querySelector(
        "#editSubtitleModal"
      ) as HTMLInputElement;
      modalHandler.checked = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateData]);

  useEffect(() => {
    window.addEventListener("keyup", handleKeyup);

    return () => {
      window.removeEventListener("keyup", handleKeyup);
    };
  }, []);

  useEffect(() => {
    if (editTaskData?.status === 200) {
      toggleTitleModal("");
      getUserstories({ id: Number(param.id!) });
    }
  }, [editTaskData, getUserstories, param.id]);
  const openConfirmModal = () => {
    const qcDoneModal = document.querySelector('#qcDoneModal') as HTMLElement;
    qcDoneModal.click();
  };
  return (
    <div className="epic-wrapper">
      <div className="info-wrapper my-2">
        <MovieInfo epicData={epicData} />
      </div>

      <div className="player rounded-lg" id="player"></div>

      <div className="form-wrapper relative">
        <div className="form-control">
          <label className="label">
            <span className="label-text">شرح مشکل</span>
          </label>
          <textarea
            id="description"
            cols={30}
            rows={3}
            ref={textareaRef}
            style={{ resize: "none" }}
            className="textarea textarea-bordered h-24"
          ></textarea>
        </div>
        <div className="flex flex-wrap md:justify-center max-w-screen-lg m-auto my-10">
          {issues.map((issue: Issue, index: number) => (
            <button
              key={issue.id}
              className={ClassNames(
                "btn ml-2 mb-2",
                index + 1 === issues.length ? "btn-error" : "btn-primary",
                issue.slug === "loadSubtitle" ? "btn-warning" : "btn-primary"
              )}
              onClick={() => handleCreateTask(issue)}
            >
              {issue.title}
            </button>
          ))}
          <button
            className="btn mb-2 ml-2 btn-primary"
            onClick={openConfirmModal}
          >
            <span className="text-xs mr-2">بدون مشکل</span>
          </button>
        </div>
      </div>

      <div className="divider"></div>

      <div className="my-10">
        {userstories?.map((userstory: UserStory) => {
          return issues.map((issue) => {
            const tasks = userstory.tasks.filter(
              (task) => task.category === issue.title
            );

            if (!tasks.length) return null;
            return (
              <div key={issue.id} className="category-wrapper">
                <h3 className="text-lg mt-20 mb-6 font-bold">{issue.title}</h3>
                <div className="flex flex-wrap">
                  {tasks.map((task) => {
                    const finalImgURL = task.image.replace(
                      imgUrl ?? "",
                      baseUrl ?? ""
                    );

                    return (
                      <div
                        key={task.id}
                        className="card w-96 bg-base-100 shadow-xl sm:ml-8 mb-8"
                      >
                        <figure>
                          <img src={finalImgURL} alt="" />
                        </figure>
                        <div className="card-body">
                          <h5>
                            {task.subject}{" "}
                            <button
                              className="btn btn-outline btn-square btn-sm"
                              onClick={() => {
                                selectedTask.current = task;
                                toggleTitleModal(task.subject);
                              }}
                            >
                              <MdModeEditOutline />
                            </button>
                          </h5>
                          <p>زمان:‌ {task.time}</p>
                          <div className="card-actions justify-end">
                            <button
                              className="btn btn-error btn-outline"
                              onClick={() => handleDeleteTask(task.id)}
                            >
                              حذف
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="divider"></div>
              </div>
            );
          });
        })}
      </div>

      <Modal.DoneConfirm onClick={handleQCDone} />

      <Modal.EditTask
        titleInputRef={titleInputRef}
        onSubmit={handleSubmitTitle}
      />

      <Modal.EditSubtitle
        subtitleInputRef={subtitleInputRef}
        onSubmit={handleSubmitSubtitle}
      />

      {isLoading && <Loader />}
    </div>
  );
}
